import { api_configuration } from "../common";
import { ObserversOfType } from "./types";
import api_cs = api_configuration.types.client_state.alan_api;
import api_modifications = api_configuration.types.modifications.alan_api;

export function addChild(parent: api_cs.IDestructable, child?: DestructorTree): DestructorTree;
export function addChild(parent: api_cs.IDestructable, child: api_cs.IDestructable): api_cs.IDestructable;
export function addChild(
	parent: api_cs.IDestructable,
	child: DestructorTree | api_cs.IDestructable = new DestructorTree()
): DestructorTree | api_cs.IDestructable {
	if (parent.destroyed) {
		child.destroy();
	} else {
		const subscription = parent.onDestroy(() => child.destroy());
		child.onDestroy(() => subscription.unsubscribe());
	}
	return child;
}

export default class DestructorTree implements api_cs.IDestructable {
	private __private__parent?: api_cs.IDestructable;
	private _children?: Set<api_cs.IDestructable>;
	private _observers?: Set<api_cs.DestructionObserver>;
	private _destroyed = false;

	static create(): DestructorTree {
		return new DestructorTree();
	}
	public get destroyed(): boolean {
		return this._destroyed;
	}

	destroy(): void {
		if (this._destroyed) return;
		this._destroyed = true;
		if (this.__private__parent) {
			(this.__private__parent as DestructorTree)._children?.delete(this);
		}
		if (this._children) {
			for (const child of this._children) {
				child.destroy();
			}
		}
		if (this._observers) {
			for (const observer of this._observers) {
				observer();
			}
			this._observers.clear();
		}
	}

	onDestroy(observer: api_cs.DestructionObserver): api_cs.Subscription {
		if (this.destroyed) {
			observer();
		} else {
			(this._observers ??= new Set<api_cs.DestructionObserver>()).add(observer);
		}
		return {
			unsubscribe: () => this._observers?.delete(observer),
		};
	}

	addChild(child?: DestructorTree): DestructorTree;
	addChild(child: api_cs.IDestructable): api_cs.IDestructable;
	addChild(child: DestructorTree | api_cs.IDestructable = new DestructorTree()): DestructorTree | api_cs.IDestructable {
		if (this.destroyed) {
			child.destroy();
		} else {
			(this._children ??= new Set<DestructorTree>()).add(child);
			(child as any).__private__parent = this;
		}
		return child;
	}

	subscribeToText<T extends api_cs.AlanNode | api_modifications.AlanNode>(
		object: T,
		subscribe_function_name: ObserversOfType<T, api_cs.TextObserver | api_modifications.TextObserver>,
		callback: (value: string, destructor: DestructorTree, first: boolean) => void,
		init = true
	): void {
		this.subscribeToValue(
			object,
			subscribe_function_name,
			(destructor, first, value) => callback(<string>value, destructor, first),
			init
		);
	}

	subscribeToNumber<T extends api_cs.AlanNode>(
		object: T,
		subscribe_function_name: ObserversOfType<T, api_cs.NumberObserver>,
		callback: (value: number, destructor: DestructorTree, first: boolean) => void,
		init = true
	): void {
		this.subscribeToValue(
			object,
			subscribe_function_name,
			(destructor, first, value) => callback(<number>value, destructor, first),
			init
		);
	}

	subscribeToStategroup<T extends api_cs.StateGroup<any>>(
		stategroup: T,
		callback: (destructor: DestructorTree, first: boolean) => void,
		init = true
	): void {
		if (stategroup.subscribe !== undefined) {
			this.subscribeToValue(stategroup, "subscribe", callback, init);
		} else if (init) {
			callback(this.addChild(), true);
		}
	}

	subscribeToCollection<T extends api_cs.AlanDictionaryEntry>(
		dictionary: api_cs.AlanDictionary<{ node: T; init: any }, any>,
		callbacks: {
			on_add?: (entry: T, destructor: DestructorTree, init: () => boolean) => void;
			on_remove?: (key: string) => void;
			on_change?: () => void;
		},
		init = false
	): void {
		if (this.destroyed || dictionary.destroyed) return;
		const children = new Map<string, DestructorTree>();
		const on_add = (entry: T, init: boolean) => {
			const key: string = entry.key_value;
			const entry_destructor = this.addChild();
			children.get(key)?.destroy();
			children.set(key, entry_destructor);
			entry.onDestroy(() => on_remove(key));
			callbacks.on_add?.(entry, entry_destructor, () => init);
			init = false;
		};
		const on_remove = (key: string) => {
			if (this.destroyed || dictionary.destroyed) return;
			children.get(key)?.destroy();
			children.delete(key);
			callbacks.on_remove?.(key);
			callbacks.on_change?.();
		};
		if (init) {
			for (const entry of dictionary.values()) {
				on_add(entry, true);
			}
			callbacks.on_change?.();
		}
		const observer = dictionary.subscribe({
			on_add: (e) => {
				if (this.destroyed) return;
				on_add(e, false);
				callbacks.on_change?.();
			},
			on_remove: (key) => {
				on_remove(key);
			},
		});
		this.onDestroy(() => {
			observer.unsubscribe();
		});
	}

	subscribeToEntry<T extends api_cs.AlanDictionaryEntry>(
		dictionary: api_cs.AlanDictionary<{ node: T; init: any }, any>,
		key: string,
		callbacks: {
			on_add?: (entry: T, destructor: DestructorTree) => void;
			on_remove?: () => void;
		},
		init = true
	): void {
		if (this.destroyed || dictionary.destroyed) return;
		let entry_destructor: DestructorTree;
		if (init) {
			if (dictionary.has(key)) {
				entry_destructor = this.addChild();
				callbacks.on_add?.(dictionary.get(key)!, entry_destructor);
			} else {
				callbacks.on_remove?.();
			}
		}
		const observer = dictionary.subscribe({
			on_add: (entry) => {
				if (this.destroyed) {
					return;
				}
				const added_key: string = entry.key_value;
				if (added_key === key) {
					entry_destructor?.destroy();
					entry_destructor = this.addChild();
					callbacks.on_add?.(entry, entry_destructor);
				}
			},
			on_remove: (removed_key) => {
				if (this.destroyed) {
					return;
				}
				if (removed_key === key) {
					entry_destructor?.destroy();
					callbacks.on_remove?.();
				}
			},
		});
		this.onDestroy(() => {
			observer.unsubscribe();
		});
	}

	private subscribeToValue<T extends (observer: api_cs.ValueObserver) => api_cs.Subscription>(
		object: api_cs.IDestructable,
		subscribe_function_name: string | number | symbol,
		callback: (destructor: DestructorTree, first: boolean, value?: string | number) => void,
		init: boolean
	) {
		if (this.destroyed || object.destroyed) return;
		let child_destructor: DestructorTree | undefined;
		let first = true;
		const observer = ((object as any)[subscribe_function_name] as T)({
			on_update: (value) => {
				if (this.destroyed) {
					return;
				}
				if (first && !init) {
					first = false;
					return;
				}
				child_destructor?.destroy();
				child_destructor = this.addChild();
				callback(child_destructor, first, value);
			},
		});
		const destruction_observer = object.onDestroy(() => child_destructor?.destroy());
		this.onDestroy(() => {
			observer.unsubscribe();
			destruction_observer.unsubscribe();
		});
	}
}
